import XLSX from "xlsx-js-style";

const norm = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const toNumber = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const cleaned = String(value)
    .replace(/\s/g, "")
    .replace(",", ".")
    .replace("%", "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

const toText = (value) => {
  if (value == null) return "";
  return String(value).trim();
};

const percent = (fact, base) => {
  const f = toNumber(fact);
  const b = toNumber(base);
  if (f == null || b == null || b === 0) return null;
  return Math.round((f / b) * 10000) / 100;
};

const formatPercent = (value) => {
  const n = toNumber(value);
  if (n == null) return "";
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

const formatNumber = (value) => {
  const n = toNumber(value);
  if (n == null) return "";
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 10000) / 10000);
};

/** Columns shown in UI / exported Excel. */
export const NATURAL_FOCI_COLUMNS = [
  { key: "index", title: "№", shortTitle: "№", group: null },
  { key: "filial", title: "Филиал", shortTitle: "Филиал", group: null },
  { key: "nozology", title: "Нозология", shortTitle: "Нозология", group: null },
  { key: "kind", title: "вид обработки (дезинсекция/ дератизация)", shortTitle: "Вид обработки", group: null },
  { key: "method", title: "способ обработки (опыление/ опрыскивание/ раскладка)", shortTitle: "Способ обработки", group: null },
  { key: "code", title: "услуга по тарификатору", shortTitle: "Код услуги", group: null },
  { key: "yearPlan", title: "Объем по плану за год, га", shortTitle: "План за год, га", group: null },
  { key: "tourVolume", title: "Объем одного тура, га", shortTitle: "Объем тура, га", group: null },
  { key: "fact", title: "Факт, га", shortTitle: "Факт, га", group: null },
  { key: "tourPercent", title: "Выполнение от одного тура, %", shortTitle: "Вып. от тура, %", group: null },
  { key: "yearPercent", title: "Выполнение от годового плана, %", shortTitle: "Вып. от плана, %", group: null },
];

/** Layout from official schedule Excel (points cols 12–14 are ignored — «на удаление»). */
const ATTACHMENT_COLUMN_MAP = {
  index: 0,
  filial: 1,
  filialDivision: 2,
  nozology: 3,
  kind: 4,
  method: 5,
  code: 6,
  yearPlan: 7,
  tourVolume: 8,
  fact: 9,
  tourPercent: 10,
  yearPercent: 11,
  tour1: 15,
  tour2: 16,
  tour3: 17,
  tour4: 18,
  tour5: 19,
  tour6: 20,
  brigades: 21,
  specialists: 22,
  vehicleEger: 23,
  vehicleUaz: 24,
  vehicleSobol: 25,
  vehicleVaz: 26,
  vehicleGaz: 27,
  vehicleLada: 28,
  equipOshu: 29,
  equipGgt: 30,
  equipGht: 31,
  equip300: 32,
  equipMotor: 33,
  equipAccum: 34,
  chemical: 35,
  unit: 36,
  amount: 37,
};

const findHeaderRows = (aoa) => {
  for (let r = 0; r < Math.min(aoa.length, 15); r++) {
    const row = (aoa[r] || []).map(norm);
    const hasFilial = row.some((c) => c.includes("филиал"));
    const hasNozology = row.some((c) => c.includes("нозология"));
    if (hasFilial && hasNozology) {
      return { headerRow: r, subHeaderRow: r + 1 };
    }
  }
  return { headerRow: 1, subHeaderRow: 2 };
};

const isAttachmentTemplate = (aoa, headerRow, subHeaderRow) => {
  const top = (aoa[headerRow] || []).map(norm);
  const sub = (aoa[subHeaderRow] || []).map(norm);
  const hasDivision = top.some((c) => c.includes("районное отделение"));
  const hasTourPct = top.some((c) => c.includes("выполнение от одного тура"));
  const hasYearPct = top.some((c) => c.includes("выполнение от годового плана"));
  const hasTour1 = sub.some((c) => c === "1 тур" || c.includes("1 тур"));
  return hasDivision && hasTourPct && hasYearPct && hasTour1;
};

const findCol = (cells, predicates) => {
  for (let i = 0; i < cells.length; i++) {
    if (predicates.some((fn) => fn(cells[i], i))) return i;
  }
  return null;
};

const buildColumnMap = (aoa, headerRow, subHeaderRow) => {
  if (isAttachmentTemplate(aoa, headerRow, subHeaderRow)) {
    return { ...ATTACHMENT_COLUMN_MAP };
  }

  const top = (aoa[headerRow] || []).map(norm);
  const sub = (aoa[subHeaderRow] || []).map(norm);
  const map = { ...ATTACHMENT_COLUMN_MAP };

  const setIfFound = (key, idx) => {
    if (idx != null) map[key] = idx;
  };

  setIfFound("filial", findCol(top, [(c) => c.includes("филиал")]));
  setIfFound("filialDivision", findCol(top, [(c) => c.includes("районное отделение")]));
  setIfFound("nozology", findCol(top, [(c) => c.includes("нозология")]));
  setIfFound("kind", findCol(top, [(c) => c.includes("вид обработки")]));
  setIfFound("method", findCol(top, [(c) => c.includes("способ обработки")]));
  setIfFound("code", findCol(top, [(c) => c.includes("тарификатор") || c.includes("код услуги")]));
  setIfFound("yearPlan", findCol(top, [(c) => c.includes("плану за год") || c.includes("план за год")]));
  setIfFound("tourVolume", findCol(top, [(c) => c.includes("одного тура") && c.includes("объем")]));
  setIfFound("fact", findCol(top, [(c) => c.startsWith("факт")]));
  setIfFound("tourPercent", findCol(top, [(c) => c.includes("выполнение от одного тура")]));
  setIfFound("yearPercent", findCol(top, [(c) => c.includes("выполнение от годового")]));

  ["tour1", "tour2", "tour3", "tour4", "tour5", "tour6"].forEach((key, i) => {
    setIfFound(key, findCol(sub, [(c) => c === `${i + 1} тур` || c.includes(`${i + 1} тур`)]));
  });

  setIfFound("brigades", findCol(sub, [(c) => c.includes("бригад")]));
  setIfFound("specialists", findCol(sub, [(c) => c.includes("специалист")]));
  setIfFound("vehicleEger", findCol(sub, [(c) => c.includes("егерь")]));
  setIfFound("vehicleUaz", findCol(sub, [(c) => c === "уаз" || c.includes("уаз")]));
  setIfFound("vehicleSobol", findCol(sub, [(c) => c.includes("соболь") || c.includes("газель")]));
  setIfFound("vehicleVaz", findCol(sub, [(c) => c === "ваз"]));
  setIfFound("vehicleGaz", findCol(sub, [(c) => c === "газ"]));
  setIfFound("vehicleLada", findCol(sub, [(c) => c.includes("лада")]));
  setIfFound("equipOshu", findCol(sub, [(c) => c === "ошу"]));
  setIfFound("equipGgt", findCol(sub, [(c) => c === "ггт"]));
  setIfFound("equipGht", findCol(sub, [(c) => c === "гхт"]));
  setIfFound("equip300", findCol(sub, [(c) => c.includes("300")]));
  setIfFound("equipMotor", findCol(sub, [(c) => c.includes("мотор")]));
  setIfFound("equipAccum", findCol(sub, [(c) => c.includes("аккум")]));
  setIfFound("chemical", findCol(sub, [(c) => c.includes("наименование")]));
  setIfFound("unit", findCol(sub, [(c) => c.includes("ед.изм") || c.includes("ед изм")]));
  setIfFound(
    "amount",
    findCol(sub, [(c) => c === "кол-во" || c === "кол во" || c.startsWith("кол-во")])
  );

  return map;
};

const pick = (row, idx) => (idx == null ? "" : row?.[idx]);

const enrichPercents = (row) => {
  const tourPercent = percent(row.fact, row.tourVolume);
  const yearPercent = percent(row.fact, row.yearPlan);
  return {
    ...row,
    tourPercent: tourPercent != null ? tourPercent : row.tourPercent,
    yearPercent: yearPercent != null ? yearPercent : row.yearPercent,
  };
};

const emptyRow = () => ({
  index: null,
  filial: "",
  filialDivision: "",
  nozology: "",
  kind: "",
  method: "",
  code: "",
  yearPlan: null,
  tourVolume: null,
  fact: null,
  tourPercent: null,
  yearPercent: null,
  tour1: "",
  tour2: "",
  tour3: "",
  tour4: "",
  tour5: "",
  tour6: "",
  brigades: null,
  specialists: null,
  vehicleEger: null,
  vehicleUaz: null,
  vehicleSobol: null,
  vehicleVaz: null,
  vehicleGaz: null,
  vehicleLada: null,
  equipOshu: null,
  equipGgt: null,
  equipGht: null,
  equip300: null,
  equipMotor: null,
  equipAccum: null,
  chemical: "",
  unit: "",
  amount: null,
});

export const parseNaturalFociExcel = (arrayBuffer) => {
  const wb = XLSX.read(arrayBuffer, { type: "array" });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) throw new Error("В файле нет листов");
  const ws = wb.Sheets[sheetName];
  const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", raw: true });
  if (!aoa.length) throw new Error("Файл пустой");

  const { headerRow, subHeaderRow } = findHeaderRows(aoa);
  const map = buildColumnMap(aoa, headerRow, subHeaderRow);
  const dataStart = Math.max(headerRow + 2, subHeaderRow + 1);

  const rows = [];
  let lastFilial = "";
  let lastFilialDivision = "";
  let lastNozology = "";
  let lastIndex = null;

  for (let r = dataStart; r < aoa.length; r++) {
    const raw = aoa[r] || [];
    const nozologyRaw = toText(pick(raw, map.nozology));
    const kind = toText(pick(raw, map.kind));
    const code = toText(pick(raw, map.code));
    const yearPlan = toNumber(pick(raw, map.yearPlan));

    if (!nozologyRaw && !kind && !code && yearPlan == null) continue;

    const rawFilial = toText(pick(raw, map.filial));
    const rawDivision = toText(pick(raw, map.filialDivision));

    // При смене филиала не переносим районное отделение от предыдущего
    if (rawFilial && rawFilial !== lastFilial) {
      lastFilial = rawFilial;
      lastFilialDivision = rawDivision;
    } else if (rawDivision) {
      lastFilialDivision = rawDivision;
    }

    const filial = rawFilial || lastFilial;
    const filialDivision = rawDivision || lastFilialDivision;
    const nozology = nozologyRaw || lastNozology;
    const indexValue = toNumber(pick(raw, map.index));
    if (nozologyRaw) lastNozology = nozologyRaw;
    if (indexValue != null) lastIndex = indexValue;

    const row = enrichPercents({
      ...emptyRow(),
      index: indexValue != null ? indexValue : lastIndex,
      filial,
      filialDivision,
      nozology,
      kind,
      method: toText(pick(raw, map.method)),
      code,
      yearPlan,
      tourVolume: toNumber(pick(raw, map.tourVolume)),
      fact: toNumber(pick(raw, map.fact)),
      tourPercent: toNumber(pick(raw, map.tourPercent)),
      yearPercent: toNumber(pick(raw, map.yearPercent)),
      tour1: toText(pick(raw, map.tour1)),
      tour2: toText(pick(raw, map.tour2)),
      tour3: toText(pick(raw, map.tour3)),
      tour4: toText(pick(raw, map.tour4)),
      tour5: toText(pick(raw, map.tour5)),
      tour6: toText(pick(raw, map.tour6)),
      brigades: toNumber(pick(raw, map.brigades)),
      specialists: toNumber(pick(raw, map.specialists)),
      vehicleEger: toNumber(pick(raw, map.vehicleEger)),
      vehicleUaz: toNumber(pick(raw, map.vehicleUaz)),
      vehicleSobol: toNumber(pick(raw, map.vehicleSobol)),
      vehicleVaz: toNumber(pick(raw, map.vehicleVaz)),
      vehicleGaz: toNumber(pick(raw, map.vehicleGaz)),
      vehicleLada: toNumber(pick(raw, map.vehicleLada)),
      equipOshu: toNumber(pick(raw, map.equipOshu)),
      equipGgt: toNumber(pick(raw, map.equipGgt)),
      equipGht: toNumber(pick(raw, map.equipGht)),
      equip300: toNumber(pick(raw, map.equip300)),
      equipMotor: toNumber(pick(raw, map.equipMotor)),
      equipAccum: toNumber(pick(raw, map.equipAccum)),
      chemical: toText(pick(raw, map.chemical)),
      unit: toText(pick(raw, map.unit)),
      amount: toNumber(pick(raw, map.amount)),
    });

    rows.push(row);
  }

  if (!rows.length) {
    throw new Error("Не удалось прочитать строки графика. Проверьте формат файла.");
  }

  return rows;
};

export const mapApiItemsToNaturalFociRows = (items = []) => {
  const rows = [];
  let lastFilial = "";
  let index = 0;

  items.forEach((item) => {
    const filial = toText(item.filialName) || "Неизвестно";
    if (filial !== lastFilial) {
      lastFilial = filial;
      index += 1;
    }
    const fact = toNumber(item.volume) ?? 0;
    const filialDivision = toText(
      item.filialDivisionName ??
        item.FilialDivisionName ??
        item.filialDivision?.name ??
        item.filialDivision?.Name ??
        item.divisionName ??
        ""
    );
    rows.push(
      enrichPercents({
        ...emptyRow(),
        index,
        filial,
        filialDivision,
        nozology: toText(item.service?.name),
        kind: toText(item.service?.kind),
        method: toText(item.service?.method),
        code: toText(item.service?.code),
        yearPlan: null,
        tourVolume: null,
        fact,
      })
    );
  });

  return rows;
};

const resolveApiDivision = (item) =>
  toText(
    item.filialDivisionName ??
      item.FilialDivisionName ??
      item.filialDivision?.name ??
      item.filialDivision?.Name ??
      item.divisionName ??
      ""
  );

export const mergeScheduleWithFacts = (scheduleRows, apiItems = []) => {
  if (!scheduleRows?.length) return mapApiItemsToNaturalFociRows(apiItems);

  const factByKey = {};
  const divisionByKey = {};
  apiItems.forEach((item) => {
    const filial = norm(item.filialName);
    const code = norm(item.service?.code);
    if (!filial || !code) return;
    const key = `${filial}|${code}`;
    factByKey[key] = (factByKey[key] || 0) + (toNumber(item.volume) || 0);
    const division = resolveApiDivision(item);
    if (division && !divisionByKey[key]) {
      divisionByKey[key] = division;
    }
  });

  // Также индекс только по коду услуги — если названия филиалов в Excel и API различаются
  const divisionByCode = {};
  const factByCode = {};
  apiItems.forEach((item) => {
    const code = norm(item.service?.code);
    if (!code) return;
    factByCode[code] = (factByCode[code] || 0) + (toNumber(item.volume) || 0);
    const division = resolveApiDivision(item);
    if (division && !divisionByCode[code]) {
      divisionByCode[code] = division;
    }
  });

  return scheduleRows.map((row) => {
    const key = `${norm(row.filial)}|${norm(row.code)}`;
    const codeKey = norm(row.code);
    const apiFact = factByKey[key] ?? factByCode[codeKey];
    const apiDivision = divisionByKey[key] ?? divisionByCode[codeKey];
    const next = { ...row };
    if (apiFact != null) next.fact = apiFact;
    if (!toText(next.filialDivision) && apiDivision) {
      next.filialDivision = apiDivision;
    }
    return enrichPercents(next);
  });
};

export const groupNaturalFociRows = (rows = []) => {
  const grouped = {};
  rows.forEach((row) => {
    const region = row.filial || "Неизвестно";
    if (!grouped[region]) grouped[region] = [];
    grouped[region].push(row);
  });
  return grouped;
};

export const sumField = (rows, field) =>
  rows.reduce((acc, row) => acc + (toNumber(row[field]) || 0), 0);

export const displayCell = (row, key) => {
  if (key === "tourPercent" || key === "yearPercent") {
    return formatPercent(row[key]);
  }
  if (
    [
      "yearPlan",
      "tourVolume",
      "fact",
      "brigades",
      "specialists",
      "vehicleEger",
      "vehicleUaz",
      "vehicleSobol",
      "vehicleVaz",
      "vehicleGaz",
      "vehicleLada",
      "equipOshu",
      "equipGgt",
      "equipGht",
      "equip300",
      "equipMotor",
      "equipAccum",
      "amount",
    ].includes(key)
  ) {
    return formatNumber(row[key]);
  }
  if (key === "index") {
    return row.index == null ? "" : String(row.index);
  }
  return row[key] ?? "";
};

const headerGroups = () => {
  const groups = [];
  let current = null;
  NATURAL_FOCI_COLUMNS.forEach((col) => {
    if (!col.group) {
      groups.push({ type: "single", col });
      current = null;
      return;
    }
    if (!current || current.title !== col.group) {
      current = { type: "group", title: col.group, cols: [col] };
      groups.push(current);
    } else {
      current.cols.push(col);
    }
  });
  return groups;
};

export const exportNaturalFociExcel = (rows, fileName) => {
  const groups = headerGroups();
  const top = [];
  const sub = [];

  groups.forEach((g) => {
    if (g.type === "single") {
      top.push(g.col.title);
      sub.push("");
    } else {
      g.cols.forEach((col, i) => {
        top.push(i === 0 ? g.title : "");
        sub.push(col.title);
      });
    }
  });

  const aoa = [top, sub];
  rows.forEach((row) => {
    aoa.push(NATURAL_FOCI_COLUMNS.map((col) => {
      const value = displayCell(row, col.key);
      if (value === "") return "";
      if (
        [
          "yearPlan",
          "tourVolume",
          "fact",
          "tourPercent",
          "yearPercent",
          "brigades",
          "specialists",
          "vehicleEger",
          "vehicleUaz",
          "vehicleSobol",
          "vehicleVaz",
          "vehicleGaz",
          "vehicleLada",
          "equipOshu",
          "equipGgt",
          "equipGht",
          "equip300",
          "equipMotor",
          "equipAccum",
          "amount",
          "index",
        ].includes(col.key)
      ) {
        const n = toNumber(value);
        return n == null ? value : n;
      }
      return value;
    }));
  });

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  const merges = [];
  let colIdx = 0;
  groups.forEach((g) => {
    if (g.type === "single") {
      merges.push({ s: { r: 0, c: colIdx }, e: { r: 1, c: colIdx } });
      colIdx += 1;
    } else {
      merges.push({
        s: { r: 0, c: colIdx },
        e: { r: 0, c: colIdx + g.cols.length - 1 },
      });
      colIdx += g.cols.length;
    }
  });
  ws["!merges"] = merges;
  ws["!cols"] = NATURAL_FOCI_COLUMNS.map((col) => ({
    width: Math.min(28, Math.max(10, col.title.length + 2)),
  }));

  XLSX.utils.book_append_sheet(wb, ws, "Природные очаги");
  XLSX.writeFile(wb, fileName);
};

export { headerGroups, toNumber };
